import { useState, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useVendors, getComplianceStatus } from "@/hooks/useVendors";
import { usePermissions } from "@/hooks/usePermissions";
import { usePagination } from "@/hooks/usePagination";
import { use1099Warnings } from "@/hooks/use1099Summary";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { NewVendorDialog } from "@/components/controller/NewVendorDialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Search, Plus, Eye, AlertTriangle, FileText, FileSpreadsheet, Printer } from "lucide-react";
import { format } from "date-fns";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { RequirePermission } from "@/components/RequirePermission";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";

const TYPE_LABELS: Record<string, string> = {
  subcontractor: "👷 Sub",
  material_supplier: "🧱 Supplier",
  equipment_rental: "🏗 Equipment",
  general: "📋 General",
};

const VendorsInner = () => {
  const [search, setSearch] = useState("");
  const pagination = usePagination();
  const { data: vendors, isLoading } = useVendors(pagination, search);
  const { data: w9Warnings = [] } = use1099Warnings();
  const navigate = useTabNavigate();
  const { can } = usePermissions();
  const [complianceIssuesOnly, setComplianceIssuesOnly] = useState(false);
  const [is1099Only, setIs1099Only] = useState(false);
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const { doExport } = useExportActions({ entityType: "vendors" });

  const filtered = useMemo(() => {
    let list = vendors || [];
    if (complianceIssuesOnly) list = list.filter((v) => getComplianceStatus(v).level !== "green");
    if (is1099Only) list = list.filter((v) => v.is_1099_eligible);
    return list;
  }, [vendors, complianceIssuesOnly, is1099Only]);

  const hasData = filtered.length > 0;

  const pageActions = [
    {
      label: "New Vendor",
      icon: Plus,
      onClick: () => setNewVendorOpen(true),
      permission: can("manage_vendors"),
    },
    {
      label: "1099 Report",
      icon: FileText,
      onClick: () => navigate("/controller/vendors/1099-report"),
    },
    {
      label: "Export to Excel",
      icon: FileSpreadsheet,
      onClick: () => hasData && doExport("excel", filtered),
      permission: hasData,
      separator: true,
    },
    {
      label: "Export to CSV",
      icon: FileText,
      onClick: () => hasData && doExport("csv", filtered),
      permission: hasData,
    },
    {
      label: "Print",
      icon: Printer,
      onClick: () => hasData && doExport("print", filtered),
      permission: hasData,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Vendors</h1>
        <PageActions actions={pageActions} />
      </div>

      {w9Warnings.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm text-destructive font-medium">
            {w9Warnings.length} vendor{w9Warnings.length > 1 ? "s" : ""} over $600 YTD with no W-9 on file —{" "}
            <button className="underline" onClick={() => navigate("/controller/vendors/1099-report")}>
              View 1099 Report
            </button>
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search vendors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch id="compliance-filter" checked={complianceIssuesOnly} onCheckedChange={setComplianceIssuesOnly} />
          <Label htmlFor="compliance-filter" className="text-sm">Compliance issues only</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="1099-filter" checked={is1099Only} onCheckedChange={setIs1099Only} />
          <Label htmlFor="1099-filter" className="text-sm">1099 Required</Label>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !filtered?.length ? (
        <p className="text-muted-foreground">No vendors found.</p>
      ) : (
        <>
          {/* Mobile card view */}
          <div className="space-y-3 md:hidden">
            {filtered.map((v) => {
              const types = v.vendor_types || [v.vendor_type || "general"];
              const cs = (v as any).compliance_status || "pending";
              const csEmoji = cs === "approved" ? "🟢" : cs === "suspended" ? "⚫" : cs === "incomplete" ? "🔴" : "🟡";
              return (
                <Card
                  key={v.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/controller/vendors/${v.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                         <div className="flex items-center gap-2 mb-1">
                          <span>{csEmoji}</span>
                          <span className="font-mono text-xs text-muted-foreground">{(v as any).vendor_number ?? ""}</span>
                          <p className="font-medium truncate">{v.name}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap mb-1">
                          {types.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-[10px]">{TYPE_LABELS[t] || t}</Badge>
                          ))}
                        </div>
                        <p className="text-sm text-muted-foreground">{v.contact_name || "No contact"}</p>
                      </div>
                      <Eye className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => {
                  const compliance = getComplianceStatus(v);
                  const types = v.vendor_types || [v.vendor_type || "general"];
                  const cs = (v as any).compliance_status || "pending";
                  const csEmoji = cs === "approved" ? "🟢" : cs === "suspended" ? "⚫" : cs === "incomplete" ? "🔴" : "🟡";
                  const payTermsLabel = v.payment_terms === "custom" && (v as any).payment_terms_custom_days
                    ? `Net ${(v as any).payment_terms_custom_days}`
                    : (v.payment_terms || "net_30").replace("_", " ");
                  return (
                    <EntityContextMenu key={v.id} entityType="vendors" entityId={v.id} data={v}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/controller/vendors/${v.id}`)}
                    >
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm">{csEmoji}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {cs === "approved"
                              ? "Compliance approved — all docs current"
                              : cs === "suspended"
                              ? "Vendor suspended"
                              : compliance.issues.length
                              ? compliance.issues.map((i) => i.label).join(", ")
                              : `Compliance: ${cs}`}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{(v as any).vendor_number ?? "—"}</TableCell>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {types.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-[10px]">{TYPE_LABELS[t] || t}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{v.contact_name || "—"}</TableCell>
                      <TableCell className="text-sm capitalize">{payTermsLabel}</TableCell>
                      <TableCell className="text-sm">{v.created_at ? format(new Date(v.created_at), "MMM d, yyyy") : "—"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/controller/vendors/${v.id}`); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    </EntityContextMenu>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <PaginationControls pagination={pagination} />
        </>
      )}

      <NewVendorDialog open={newVendorOpen} onOpenChange={setNewVendorOpen} />
    </div>
  );
};

const Vendors = () => (
  <RequirePermission anyOf={["manage_vendors", "view_projects"]}>
    <VendorsInner />
  </RequirePermission>
);

export default Vendors;
